import { AccountSelector, Input, Select } from '@components'
import { ACC_TYPES_BANK } from '@constants'
import { IWorkflowFilterFieldOption, IWorkflowFilterTypeOption } from '@query'
import MultipleContactSelector from '@pages/SettingsPage/sections/AutomationSettings/AccountingRule/Form/ContactSelector/MultipleContactSelector'
import React, { useEffect, useState } from 'react'
import { Control, Controller, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FaCalendarDay, FaClock } from 'react-icons/fa'
import { FilterCondition, FilterNodeConfig } from '../nodes/types'
import {
  formatRelativeCutoff,
  getAbsoluteDatetimeValue,
  getRelativeDatetimeValue,
  isValidRelativeAmount
} from './filterDateUtils'
import { optionIncludesValue } from './formOptionUtils'
import {
  DEFAULT_RELATIVE_DATETIME,
  fromDatetimeLocalValue,
  getFieldSpec,
  isRelativeDatetimeValue,
  toDatetimeLocalValue
} from '../nodes/filterConfigUtils'
import {
  BooleanControlRow,
  ConditionBody,
  DatetimeControlRow,
  FieldLabel,
  FieldSelect,
  Footnote,
  FootnoteIcon,
  StackedValue,
  TrailingWord,
  ValueControl
} from './filterNodeConfigForm.styles'

export interface ConditionFieldsProps {
  typeSpec: IWorkflowFilterTypeOption | undefined
  control: Control<FilterNodeConfig>
  index: number
  onUpdate: (index: number, patch: Partial<FilterCondition>) => void
}

interface FieldSelectControllerProps {
  typeSpec: IWorkflowFilterTypeOption
  control: Control<FilterNodeConfig>
  index: number
  ariaLabel: string
  idPrefix: string
  onUpdate: (index: number, patch: Partial<FilterCondition>) => void
}

const FieldSelectController: React.FC<FieldSelectControllerProps> = ({
  typeSpec,
  control,
  index,
  ariaLabel,
  idPrefix,
  onUpdate
}) => (
  <FieldSelect>
    <Controller
      name={`conditions.${index}.field`}
      control={control}
      render={({ field, fieldState }) => (
        <Select
          id={`${idPrefix}-${index}`}
          name={`${idPrefix}-${index}`}
          aria-label={ariaLabel}
          value={field.value}
          errors={fieldState.error}
          onChange={event => onUpdate(index, { field: event.target.value })}
          onBlur={field.onBlur}
        >
          {typeSpec.fields.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
          {!optionIncludesValue(typeSpec.fields, field.value) && field.value && (
            <option value={field.value}>{field.value}</option>
          )}
        </Select>
      )}
    />
  </FieldSelect>
)

export const ConditionFields: React.FC<ConditionFieldsProps> = ({
  typeSpec,
  control,
  index,
  onUpdate
}) => {
  const [t] = useTranslation()
  const conditionField = useWatch({
    control,
    name: `conditions.${index}.field`
  })
  const conditionOperator = useWatch({
    control,
    name: `conditions.${index}.operator`
  })
  const conditionValue = useWatch({
    control,
    name: `conditions.${index}.value`
  })
  const currentFieldSpec = getFieldSpec(typeSpec, conditionField)
  if (!typeSpec) return null

  if (!currentFieldSpec) {
    return (
      <ConditionBody>
        <FieldSelectController
          typeSpec={typeSpec}
          control={control}
          index={index}
          ariaLabel={conditionField}
          idPrefix="workflow-filter-unknown-field"
          onUpdate={onUpdate}
        />
      </ConditionBody>
    )
  }

  if (currentFieldSpec.type === 'boolean') {
    return (
      <BooleanControlRow>
        <FieldSelectController
          typeSpec={typeSpec}
          control={control}
          index={index}
          ariaLabel={currentFieldSpec.label}
          idPrefix="workflow-filter-field"
          onUpdate={onUpdate}
        />

        <ValueControl>
          <Controller
            name={`conditions.${index}.value`}
            control={control}
            render={({ field, fieldState }) => (
              <Select
                id={`workflow-filter-value-${index}`}
                name={`workflow-filter-value-${index}`}
                aria-label={currentFieldSpec.label}
                value={field.value === true ? 'true' : 'false'}
                errors={fieldState.error}
                onChange={event => onUpdate(index, { value: event.target.value === 'true' })}
                onBlur={field.onBlur}
              >
                {currentFieldSpec.choices.map(option => (
                  <option key={String(option.value)} value={String(option.value)}>
                    {option.label}
                  </option>
                ))}
              </Select>
            )}
          />
        </ValueControl>
      </BooleanControlRow>
    )
  }

  if (
    currentFieldSpec.type === 'text' ||
    currentFieldSpec.type === 'account' ||
    currentFieldSpec.type === 'contact' ||
    currentFieldSpec.type === 'amount'
  ) {
    return (
      <BankTransactionConditionFields
        typeSpec={typeSpec}
        control={control}
        index={index}
        currentFieldSpec={currentFieldSpec}
        conditionOperator={conditionOperator}
        conditionValue={conditionValue}
        onUpdate={onUpdate}
        t={t}
      />
    )
  }

  return (
    <DatetimeConditionFields
      typeSpec={typeSpec}
      control={control}
      index={index}
      currentFieldSpec={currentFieldSpec}
      conditionOperator={conditionOperator}
      conditionValue={conditionValue}
      onUpdate={onUpdate}
      t={t}
    />
  )
}

interface BankTransactionConditionFieldsProps {
  typeSpec: IWorkflowFilterTypeOption
  control: Control<FilterNodeConfig>
  index: number
  currentFieldSpec: IWorkflowFilterFieldOption
  conditionOperator: string
  conditionValue: FilterCondition['value']
  onUpdate: (index: number, patch: Partial<FilterCondition>) => void
  t: ReturnType<typeof useTranslation>[0]
}

const textPlaceholderKey = (field: string): string =>
  field === 'reference'
    ? 'settings.automation.accountingRules.form.referencePlaceholder'
    : 'settings.automation.accountingRules.form.descriptionPlaceholder'

const COMPLETE_AMOUNT = /^-?\d+(\.\d+)?$/

const parseAmountInput = (raw: string): number | null => {
  const normalized = raw.trim().replaceAll(',', '.')
  if (!normalized) return null
  if (!COMPLETE_AMOUNT.test(normalized)) return Number.NaN
  return Number(normalized)
}

const amountDraftFromValue = (value: FilterCondition['value']): string =>
  typeof value === 'number' && Number.isFinite(value) ? String(value) : ''

interface AmountValueInputProps {
  index: number
  value: FilterCondition['value']
  onUpdate: (index: number, patch: Partial<FilterCondition>) => void
}

const AmountValueInput: React.FC<AmountValueInputProps> = ({ index, value, onUpdate }) => {
  const committedDraft = amountDraftFromValue(value)
  const [amountDraft, setAmountDraft] = useState(committedDraft)
  const [isFocused, setIsFocused] = useState(false)

  if (!isFocused && amountDraft !== committedDraft) {
    setAmountDraft(committedDraft)
  }

  return (
    <Input
      id={`workflow-filter-amount-${index}`}
      name={`workflow-filter-amount-${index}`}
      inputMode="decimal"
      autoComplete="off"
      value={amountDraft}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      onChange={event => {
        const raw = event.target.value
        setAmountDraft(raw)
        const parsed = parseAmountInput(raw)
        if (parsed === null) {
          onUpdate(index, { value: null })
          return
        }
        if (!Number.isNaN(parsed)) {
          onUpdate(index, { value: parsed })
        }
      }}
    />
  )
}

const BankTransactionConditionFields: React.FC<BankTransactionConditionFieldsProps> = ({
  typeSpec,
  control,
  index,
  currentFieldSpec,
  conditionOperator,
  conditionValue,
  onUpdate,
  t
}) => {
  const showOperator = currentFieldSpec.operators.length > 1
  const textValue = typeof conditionValue === 'string' ? conditionValue : ''
  const accountId = typeof conditionValue === 'number' && conditionValue > 0 ? conditionValue : null
  const contactIds = Array.isArray(conditionValue)
    ? conditionValue.filter((item): item is number => typeof item === 'number')
    : []

  return (
    <ConditionBody>
      <BooleanControlRow>
        <FieldSelectController
          typeSpec={typeSpec}
          control={control}
          index={index}
          ariaLabel={currentFieldSpec.label}
          idPrefix="workflow-filter-bank-field"
          onUpdate={onUpdate}
        />
        {showOperator && (
          <FieldSelect>
            <Select
              id={`workflow-filter-operator-${index}`}
              name={`workflow-filter-operator-${index}`}
              aria-label={currentFieldSpec.label}
              value={conditionOperator}
              onChange={event => onUpdate(index, { operator: event.target.value })}
            >
              {!optionIncludesValue(currentFieldSpec.operators, conditionOperator) &&
                conditionOperator && <option value={conditionOperator}>{conditionOperator}</option>}
              {currentFieldSpec.operators.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </FieldSelect>
        )}
      </BooleanControlRow>

      <StackedValue>
        {currentFieldSpec.type === 'text' && (
          <Input
            id={`workflow-filter-text-${index}`}
            name={`workflow-filter-text-${index}`}
            placeholder={t(textPlaceholderKey(currentFieldSpec.value))}
            value={textValue}
            onChange={event => onUpdate(index, { value: event.target.value })}
          />
        )}
        {currentFieldSpec.type === 'account' && (
          <AccountSelector
            id={`workflow-filter-account-${index}`}
            name={`workflow-filter-account-${index}`}
            accountFilter={
              currentFieldSpec.value === 'payment_account' ? { type: [...ACC_TYPES_BANK] } : {}
            }
            value={accountId}
            onChange={nextAccountId => onUpdate(index, { value: nextAccountId })}
          />
        )}
        {currentFieldSpec.type === 'contact' && (
          <MultipleContactSelector
            value={contactIds}
            onChange={nextContactIds => onUpdate(index, { value: nextContactIds })}
          />
        )}
        {currentFieldSpec.type === 'amount' && (
          <AmountValueInput index={index} value={conditionValue} onUpdate={onUpdate} />
        )}
      </StackedValue>

      {currentFieldSpec.type === 'text' && conditionOperator === 'cnt' && (
        <Footnote>
          <span>{t('settings.automation.accountingRules.form.conditionWildcard')}</span>
        </Footnote>
      )}
    </ConditionBody>
  )
}

type DatetimeKind = 'relative' | 'absolute'

interface DatetimeConditionFieldsProps {
  typeSpec: IWorkflowFilterTypeOption
  control: Control<FilterNodeConfig>
  index: number
  currentFieldSpec: IWorkflowFilterFieldOption
  conditionOperator: string
  conditionValue: FilterCondition['value']
  onUpdate: (index: number, patch: Partial<FilterCondition>) => void
  t: ReturnType<typeof useTranslation>[0]
}

const defaultValueForKind = (kind: DatetimeKind): FilterCondition['value'] =>
  kind === 'relative' ? DEFAULT_RELATIVE_DATETIME : new Date().toISOString()

const DatetimeConditionFields: React.FC<DatetimeConditionFieldsProps> = ({
  typeSpec,
  control,
  index,
  currentFieldSpec,
  conditionOperator,
  conditionValue,
  onUpdate,
  t
}) => {
  const isRelative = isRelativeDatetimeValue(conditionValue)
  const datetimeKind: DatetimeKind = isRelative ? 'relative' : 'absolute'
  const relativeValue = getRelativeDatetimeValue(conditionValue)
  const [amountDraft, setAmountDraft] = useState(String(relativeValue.amount))
  const [amountError, setAmountError] = useState<string | null>(null)

  useEffect(() => {
    if (!isRelative) return
    setAmountDraft(String(relativeValue.amount))
    setAmountError(null)
  }, [isRelative, relativeValue.amount])

  const selectOperator = (operator: string) => {
    onUpdate(index, { operator })
  }

  const selectKind = (kind: DatetimeKind) => {
    if (kind === datetimeKind) return
    onUpdate(index, { value: defaultValueForKind(kind) })
  }

  const commitAmount = (rawValue: string) => {
    setAmountDraft(rawValue)

    if (!isValidRelativeAmount(rawValue)) {
      setAmountError(t('workflows.config.filter.relativeAmountInvalid'))
      return
    }

    setAmountError(null)
    onUpdate(index, {
      value: {
        kind: 'relative',
        amount: Math.floor(Number(rawValue)),
        unit: relativeValue.unit
      }
    })
  }

  const cutoff = formatRelativeCutoff(relativeValue.amount, relativeValue.unit)

  return (
    <ConditionBody>
      <DatetimeControlRow>
        {typeSpec.fields.length > 1 ? (
          <FieldSelectController
            typeSpec={typeSpec}
            control={control}
            index={index}
            ariaLabel={currentFieldSpec.label}
            idPrefix="workflow-filter-datetime-field"
            onUpdate={onUpdate}
          />
        ) : (
          <FieldLabel>{currentFieldSpec.label}</FieldLabel>
        )}

        <FieldSelect>
          <Select
            id={`workflow-filter-operator-${index}`}
            name={`workflow-filter-operator-${index}`}
            aria-label={t('workflows.config.filter.dateMode')}
            value={conditionOperator}
            onChange={event => selectOperator(event.target.value)}
          >
            {!optionIncludesValue(currentFieldSpec.operators, conditionOperator) &&
              conditionOperator && <option value={conditionOperator}>{conditionOperator}</option>}
            {currentFieldSpec.operators.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </FieldSelect>

        <FieldSelect>
          <Select
            id={`workflow-filter-datetime-kind-${index}`}
            name={`workflow-filter-datetime-kind-${index}`}
            aria-label={t('workflows.config.filter.dateMode')}
            value={datetimeKind}
            onChange={event => selectKind(event.target.value as DatetimeKind)}
          >
            <option value="relative">{t('workflows.config.filter.compareGroups.relative')}</option>
            <option value="absolute">{t('workflows.config.filter.compareGroups.absolute')}</option>
          </Select>
        </FieldSelect>

        {isRelative ? (
          <>
            <ValueControl>
              <Input
                id={`workflow-filter-relative-amount-${index}`}
                name={`workflow-filter-relative-amount-${index}`}
                type="number"
                min={1}
                step={1}
                label={t('workflows.config.filter.relativeAmount')}
                value={amountDraft}
                errors={amountError ? { message: amountError } : undefined}
                onChange={event => commitAmount(event.target.value)}
                onBlur={event => {
                  if (!isValidRelativeAmount(event.target.value)) {
                    setAmountError(t('workflows.config.filter.relativeAmountInvalid'))
                    setAmountDraft(String(relativeValue.amount))
                  }
                }}
              />
            </ValueControl>
            <ValueControl>
              <Select
                id={`workflow-filter-relative-unit-${index}`}
                name={`workflow-filter-relative-unit-${index}`}
                label={t('workflows.config.filter.relativeUnit')}
                value={relativeValue.unit}
                onChange={event =>
                  onUpdate(index, {
                    value: {
                      kind: 'relative',
                      amount: relativeValue.amount,
                      unit: event.target.value
                    }
                  })
                }
              >
                <option value="minutes">
                  {t('workflows.config.filter.relativeUnits.minutes', {
                    count: relativeValue.amount
                  })}
                </option>
                <option value="hours">
                  {t('workflows.config.filter.relativeUnits.hours', {
                    count: relativeValue.amount
                  })}
                </option>
                <option value="days">
                  {t('workflows.config.filter.relativeUnits.days', { count: relativeValue.amount })}
                </option>
              </Select>
            </ValueControl>
            <TrailingWord>{t('workflows.config.filter.relativeAgoWord')}</TrailingWord>
          </>
        ) : (
          <ValueControl>
            <Controller
              name={`conditions.${index}.value`}
              control={control}
              render={({ field }) => (
                <Input
                  id={`workflow-filter-absolute-datetime-${index}`}
                  name={`workflow-filter-absolute-datetime-${index}`}
                  type="datetime-local"
                  label={t('workflows.config.filter.dateModeAbsolute')}
                  value={toDatetimeLocalValue(getAbsoluteDatetimeValue(field.value))}
                  onChange={event =>
                    onUpdate(index, { value: fromDatetimeLocalValue(event.target.value) })
                  }
                  onBlur={field.onBlur}
                />
              )}
            />
          </ValueControl>
        )}
      </DatetimeControlRow>

      <Footnote>
        {isRelative ? (
          <>
            <FootnoteIcon>
              <FaClock />
            </FootnoteIcon>
            <span>{t('workflows.config.filter.relativeHint', { cutoff })}</span>
          </>
        ) : (
          <>
            <FootnoteIcon>
              <FaCalendarDay />
            </FootnoteIcon>
            <span>{t('workflows.config.filter.absoluteHint')}</span>
          </>
        )}
      </Footnote>
    </ConditionBody>
  )
}
